import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { track } from '@eventbrite/legacy-infra.datalayer-library';

import styles from './RelatedEventCard.module.scss';
import { UrgencySignalBadge } from '@/components/UrgencySignals/UrgencySignalBadge';
import { useEventCardComponents } from './hooks/useEventCardComponents';
import { useFormattedEventData } from './hooks/useFormattedEventData';
import type { RelatedEvent } from '../types';
import { transformRelatedEventToTrackingEvent } from '@/utils/mappers';
import type { AffiliateCodes } from '@/components/_shared/constants';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';
import { getImageProps } from 'next/image';
export interface RelatedEventCardProps {
  relatedEvent: RelatedEvent;
  affiliateCode?: AffiliateCodes;
  /**
   * Optional fan-out fired synchronously inside `handleCardClick`, before the
   * destination tab is opened, so the parent (`RelatedEvents`) can emit the
   * PicoX `eb_event_page_opened` recommendation-click event without dropping
   * it on the about-to-unload page.
   */
  onCardClick?: (event: RelatedEvent) => void;
}

export const RelatedEventCard: React.FC<RelatedEventCardProps> = ({ relatedEvent, affiliateCode, onCardClick }) => {
  const { formatMessage } = useIntl();
  const components = useEventCardComponents();
  const formattedData = useFormattedEventData(relatedEvent);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS - CRITICAL FOR REACT RULES
  const titleClass = useMemo(() => {
    return formattedData?.urgencySignalBadge
      ? classNames(styles.title, { [styles.linegap]: !!formattedData.urgencySignalBadge })
      : styles.title;
  }, [formattedData?.urgencySignalBadge]);

  const handleCardClick = useCallback(() => {
    if (!formattedData?.url || formattedData.url === '#') {
      return;
    }

    track({
      eventName: LISTING_HEAP_IDS.LISTING_RELATED_EVENT_CLICK,
      eventData: {
        event_id: relatedEvent.id,
        event_name: relatedEvent.name,
      },
    });

    // PicoX `eb_event_page_opened` fan-out — fire BEFORE `window.open` so the
    // event isn't dropped on the about-to-unload page.
    onCardClick?.(relatedEvent);

    //if affiliateCode is provided, append it to the URL
    if (affiliateCode) {
      const parsedUrl = new URL(formattedData.url);
      parsedUrl.searchParams.set('aff', affiliateCode);
      window.open(parsedUrl.toString(), '_blank');
    } else {
      window.open(formattedData.url, '_blank');
    }
  }, [formattedData?.url, affiliateCode, onCardClick, relatedEvent]);

  // Early returns ONLY after all hooks have been called
  if (!relatedEvent || !components || !formattedData) {
    return null;
  }

  // Extract components - safe to do after early returns since components is checked above
  const {
    VerticalEventCard,
    EventCardPrice,
    EventCardLocation,
    EventCardDate,
    EventCardTitle,
    EventCardImage,
    EventCardPromoted,
    EventCardTracking,
  } = components;

  const {
    props: { src },
  } = getImageProps({
    alt: '',
    width: 148,
    height: 74,
    src: formattedData.imageUrl || '',
  });

  const imageElement = EventCardImage ? (
    <EventCardImage src={src} alt={formattedData.name || formatMessage({ id: 'Event image' })} />
  ) : null;

  // Create event data for tracking
  const eventForTracking = transformRelatedEventToTrackingEvent(relatedEvent);

  const cardContent = (
    <div
      className={styles.grid}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`${formatMessage({ id: 'Open event' })}: ${formattedData.name || formatMessage({ id: 'Unnamed event' })}`}
      style={{ cursor: 'pointer' }}
    >
      {formattedData.urgencySignalBadge && (
        <div className={styles.signalBadgeWrapper}>
          <UrgencySignalBadge badge={formattedData.urgencySignalBadge} />
        </div>
      )}

      {EventCardTitle && formattedData.name && (
        <div className={styles.titleWrapper}>
          <EventCardTitle title={formattedData.name} className={titleClass} />
        </div>
      )}

      {EventCardDate && formattedData.formattedEventDateTimeString && (
        <div className={styles.dateWrapper}>
          <EventCardDate formattedDateString={formattedData.formattedEventDateTimeString} className={styles.date} />
        </div>
      )}

      {EventCardLocation && (
        <div className={styles.locationWrapper}>
          <EventCardLocation
            formattedLocationString={formattedData.locationString}
            isOnlineEvent={formattedData.isOnlineEvent}
            className={styles.location}
          />
        </div>
      )}

      {EventCardPrice && (
        <div className={styles.priceWrapper}>
          <EventCardPrice
            isFree={formattedData.isFree}
            formattedPriceString={formattedData.priceString}
            className={styles.price}
          />
        </div>
      )}

      {formattedData.isPromoted && EventCardPromoted && (
        <div className={styles.promotedWrapper} data-testid="promoted-event-card">
          <EventCardPromoted isPromoted={formattedData.isPromoted} className={styles.promoted} />
        </div>
      )}

      <div className={styles.imageWrapper}>{imageElement}</div>
    </div>
  );

  return (
    <div data-testid="related-event-card" className={styles.wrapper}>
      <VerticalEventCard isRaised={false}>
        {EventCardTracking ? (
          <EventCardTracking event={eventForTracking}>{cardContent}</EventCardTracking>
        ) : (
          cardContent
        )}
      </VerticalEventCard>
    </div>
  );
};
